<script setup lang="ts">
    import { type ColumnDefinition } from "@cmnhospitals/components-vue";
    import { CmnhDataGrid } from "@cmnhospitals/components-vue/datagrid";
    import { LocationParent, Location, ReceivePaymentTransaction } from "@/typings";
    import {
        createPaymentLinkFormat,
        currencyFormatter,
        dateFormatter,
        officeChipsFormatter,
        receiveLinkFormat
    } from "@/composables/tabulatorFormatters";
    import { sanitizeMoney } from "@/utils";
    import { DateTime } from "luxon";
    import Spinner from "@/components/common/Spinner.vue";
    import { computed, nextTick, ref, watch } from "vue";

    const props = defineProps<{
        paymentsData: ReceivePaymentTransaction[];
        parentsData: LocationParent[];
        locationsData: Location[];
        frontendFilters: Record<string, string>;
        matchedAssociateNames: Set<string> | null;
        matchedPrimaryLocationIds: Set<string> | null;
        matchedPrimaryGroupLocationIds: Set<string> | null;
        isSearching?: boolean;
    }>();

    const columns: ColumnDefinition[] = [
        {
            title: "Date Submitted",
            field: "createdDate",
            headerSort: true,
            formatter: dateFormatter,
            minWidth: 165,
            sorter: (a: any, b: any) =>
                DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis(),
            titleFormatter: () => `
                <span>
                    Date Submitted
                    <i class="glyphicon glyphicon-info-sign" data-toggle="tooltip" data-placement="bottom" title="All times are shown in Central Time"></i>
                </span>`
        },
        {
            title: "Payment #",
            field: "offlineTransactionNumber",
            headerSort: true,
            minWidth: 120
        },
        {
            title: "Office(s)",
            field: "offices",
            headerSort: true,
            formatter: officeChipsFormatter,
            minWidth: 150
        },
        {
            title: "Check Number",
            field: "checkNumber",
            headerSort: true,
            formatter: "textarea",
            minWidth: 155
        },
        {
            title: "Payment Amount",
            field: "transactionAmount",
            headerSort: true,
            formatter: currencyFormatter,
            minWidth: 110
        },
        {
            title: "Created By",
            field: "createdBy",
            headerSort: true,
            minWidth: 110
        },
        {
            title: "OfflineTransactionId",
            field: "offlineTransactionId",
            visible: false
        },
        {
            title: "",
            field: "receivePayment",
            formatter: (cell: any) => {
                const offlineTransactionId = cell.getRow().getData().offlineTransactionId;
                return receiveLinkFormat(offlineTransactionId);
            },
            headerSort: false,
            minWidth: 130
        }
    ];

    const parentLocationColumns: ColumnDefinition[] = [
        {
            title: "Office Name",
            field: "locationName",
            headerSort: true,
            minWidth: 150
        },
        {
            title: "Office ID",
            field: "locationNumber",
            headerSort: true,
            minWidth: 150
        },
        {
            title: "Offices",
            field: "offices",
            headerSort: true,
            formatter: officeChipsFormatter,
            minWidth: 150
        },
        {
            title: "",
            field: "createPayment",
            formatter: (cell: any) => {
                const locationId = cell.getRow().getData().locationId;
                return createPaymentLinkFormat(locationId, true);
            },
            headerSort: false,
            minWidth: 130
        }
    ];

    const locationColumns: ColumnDefinition[] = [
        {
            title: "Office Name",
            field: "locationName",
            headerSort: true,
            minWidth: 150
        },
        {
            title: "Office ID",
            field: "locationNumber",
            headerSort: true,
            minWidth: 150
        },
        {
            title: "Address",
            field: "address",
            headerSort: true,
            formatter: "textarea",
            minWidth: 150
        },
        {
            title: "",
            field: "createPayment",
            formatter: (cell: any) => {
                const locationId = cell.getRow().getData().locationId;
                return createPaymentLinkFormat(locationId);
            },
            headerSort: false,
            minWidth: 130
        }
    ];

    // Build a locationId -> Location lookup for fast city/state checks.
    // Keys lowercased because SQL Server's CONVERT(VARCHAR(36), guid) can return
    // uppercase while C# Guid serialization is lowercase, so the casings differ.
    const locationLookup = computed(() => {
        const map = new Map<string, Location>();
        for (const loc of props.locationsData) {
            map.set(loc.locationId.toLowerCase(), loc);
        }
        return map;
    });

    function transactionLocations(transaction: ReceivePaymentTransaction): Location[] {
        if (!transaction.locationIds) return [];
        return transaction.locationIds
            .split(",")
            .map(id => locationLookup.value.get(id.trim().toLowerCase()))
            .filter((l): l is Location => !!l);
    }

    function provinceMatches(province: string | null | undefined, filterValue: string): boolean {
        const p = (province ?? "").trim().toLowerCase();
        const f = filterValue.trim().toLowerCase();
        if (!p || !f) return false;
        // Match against either an abbreviation column ("CO") or a full-name column ("Colorado").
        return p === f || p.startsWith(f);
    }

    function matchesFrontendFilters(transaction: ReceivePaymentTransaction): boolean {
        const f = props.frontendFilters;
        const offices = (transaction.offices ?? "").toLowerCase();

        if (f.locationName && !offices.includes(f.locationName.toLowerCase())) return false;
        if (f.locationNumber && !offices.includes(f.locationNumber.toLowerCase())) return false;
        if (f.offlineTransactionNumber && !(transaction.offlineTransactionNumber ?? "").toLowerCase().includes(f.offlineTransactionNumber.toLowerCase())) return false;
        if (f.checkNumber && !(transaction.checkNumber ?? "").toLowerCase().includes(f.checkNumber.toLowerCase())) return false;
        if (f.amount) {
            // sanitizeMoney strips "$", commas, "#", etc.; non-numeric input (e.g. "Detroit") -> "" -> matches nothing.
            const cleaned = sanitizeMoney(f.amount);
            if (!cleaned || transaction.transactionAmount !== parseFloat(cleaned)) return false;
        }

        if (f.locationCity || f.locationState) {
            const locs = transactionLocations(transaction);
            if (f.locationCity && !locs.some(l => (l.city ?? "").toLowerCase().includes(f.locationCity.toLowerCase()))) return false;
            if (f.locationState && !locs.some(l => provinceMatches(l.province, f.locationState))) return false;
        }

        if (f.startDate || f.endDate) {
            const created = DateTime.fromISO(transaction.createdDate);
            if (f.startDate && created < DateTime.fromISO(f.startDate)) return false;
            if (f.endDate && created > DateTime.fromISO(f.endDate)) return false;
        }

        return true;
    }

    function matchesAssociate(transaction: ReceivePaymentTransaction): boolean {
        if (!props.matchedAssociateNames) return true;
        return props.matchedAssociateNames.has(transaction.createdBy ?? "");
    }

    // For state/city filters, a parent qualifies if any of its child offices match.
    const childrenByParentId = computed(() => {
        const map = new Map<string, Location[]>();
        for (const loc of props.locationsData) {
            if (!loc.parentId) continue;
            const arr = map.get(loc.parentId) ?? [];
            arr.push(loc);
            map.set(loc.parentId, arr);
        }
        return map;
    });

    // Search fields that only describe a payment — offices and parent offices have no
    // equivalent column. When any of these is active an office can never be a valid
    // result, so those tabs must show zero. Without this the office/parent lists
    // ignored these fields and returned every row (e.g. typing "Detroit" into
    // Payment # or Check Number listed all offices). See R20-1442.
    const PAYMENT_ONLY_FIELDS = ["offlineTransactionNumber", "checkNumber", "amount", "startDate", "endDate"];

    const hasPaymentOnlyFilter = computed(() =>
        PAYMENT_ONLY_FIELDS.some(field => !!props.frontendFilters[field])
    );

    function matchesParent(parent: LocationParent): boolean {
        const f = props.frontendFilters;
        const name = (parent.locationName ?? "").toLowerCase();
        const num = (parent.locationNumber ?? "").toLowerCase();
        const childOffices = (parent.offices ?? "").toLowerCase();

        if (f.locationName) {
            const v = f.locationName.toLowerCase();
            if (!name.includes(v) && !childOffices.includes(v)) return false;
        }
        if (f.locationNumber) {
            const v = f.locationNumber.toLowerCase();
            if (!num.includes(v) && !childOffices.includes(v)) return false;
        }

        if (f.locationCity || f.locationState) {
            const children = childrenByParentId.value.get(parent.locationId) ?? [];
            if (f.locationCity) {
                const v = f.locationCity.toLowerCase();
                if (!children.some(c => (c.city ?? "").toLowerCase().includes(v))) return false;
            }
            if (f.locationState && !children.some(c => provinceMatches(c.province, f.locationState))) return false;
        }

        return true;
    }

    function matchesOffice(location: Location): boolean {
        const f = props.frontendFilters;
        const name = (location.locationName ?? "").toLowerCase();
        const num = (location.locationNumber ?? "").toLowerCase();
        const city = (location.city ?? "").toLowerCase();

        if (f.locationName && !name.includes(f.locationName.toLowerCase())) return false;
        if (f.locationNumber && !num.includes(f.locationNumber.toLowerCase())) return false;
        if (f.locationCity && !city.includes(f.locationCity.toLowerCase())) return false;
        if (f.locationState && !provinceMatches(location.province, f.locationState)) return false;
        return true;
    }

    const filteredPayments = computed(() =>
        props.paymentsData.filter(t => matchesAssociate(t) && matchesFrontendFilters(t))
    );

    const filteredParents = computed(() => {
        if (hasPaymentOnlyFilter.value) return [];
        let arr = props.parentsData;
        if (props.matchedPrimaryGroupLocationIds) {
            const ids = props.matchedPrimaryGroupLocationIds;
            arr = arr.filter(p => ids.has(p.locationId));
        }
        return arr.filter(matchesParent);
    });

    const filteredLocations = computed(() => {
        if (hasPaymentOnlyFilter.value) return [];
        let arr = props.locationsData;
        if (props.matchedPrimaryLocationIds) {
            const ids = props.matchedPrimaryLocationIds;
            arr = arr.filter(l => ids.has(l.locationId));
        }
        return arr.filter(matchesOffice);
    });

    // CmnhDataGrid types rowData as Record<string, unknown>[]; cast our typed arrays through.
    type GridRow = Record<string, unknown>;
    const paymentRows = computed(() => filteredPayments.value as unknown as GridRow[]);
    const parentRows = computed(() => filteredParents.value as unknown as GridRow[]);
    const locationRows = computed(() => filteredLocations.value as unknown as GridRow[]);

    // Grid refs
    const paymentGridRef = ref<any>(null);
    const parentLocationsGridRef = ref<any>(null);
    const locationGridRef = ref<any>(null);

    // Counters derive directly from the filtered lists so they stay accurate
    // even when an empty tab unmounts its grid (no count-change event to listen to).
    const paymentCounter = computed(() => filteredPayments.value.length);
    const parentLocationCounter = computed(() => filteredParents.value.length);
    const locationCounter = computed(() => filteredLocations.value.length);

    const tabLabels = computed(() => [
        { label: "Payments", count: paymentCounter.value },
        { label: "Parent Offices", count: parentLocationCounter.value },
        { label: "Offices", count: locationCounter.value }
    ]);

    const hasPayments = computed(() => filteredPayments.value.length > 0);
    const hasParents = computed(() => filteredParents.value.length > 0);
    const hasLocations = computed(() => filteredLocations.value.length > 0);

    const selectedTab = ref(0);

    function safeRedraw(grid: { redraw?: (force: boolean) => void } | null) {
        if (!grid?.redraw) return;
        try {
            grid.redraw(true);
        } catch {
            // Tabulator's async _create may not have finished yet; layout will settle on its own.
        }
    }

    watch(selectedTab, async (idx) => {
        await nextTick();
        if (idx === 0) safeRedraw(paymentGridRef.value);
        else if (idx === 1) safeRedraw(parentLocationsGridRef.value);
        else if (idx === 2) safeRedraw(locationGridRef.value);
    });

    // After a search completes, jump to the first tab with results
    watch(
        () => [paymentCounter.value, parentLocationCounter.value, locationCounter.value],
        ([payments, parents, locations]) => {
            const counters = [payments, parents, locations];
            const firstWithResults = counters.findIndex(c => c > 0);
            if (firstWithResults !== -1 && selectedTab.value !== firstWithResults && counters[selectedTab.value] === 0) {
                selectedTab.value = firstWithResults;
            }
        }
    );
</script>

<template>
    <div class="tabs">
        <ul class="nav nav-tabs">
            <li v-for="(tab, idx) in tabLabels" :key="idx" :class="{ active: selectedTab === idx }" @click="selectedTab = idx">
                <a href="#" @click.prevent>
                    {{ tab.label }}
                    <span class="badge">{{ tab.count }}</span>
                </a>
            </li>
        </ul>
    </div>

    <!-- Payments -->
    <div v-show="selectedTab === 0">
        <Spinner :show="isSearching" label="Searching..." />
        <template v-if="!isSearching">
            <div v-if="!hasPayments" class="panel well empty-notice">
                <p><span class="glyphicon glyphicon-remove-circle" aria-hidden="true"></span></p>
                <h2>No payments to display.</h2>
                <p>Do you need to adjust your filters above?</p>
            </div>
            <CmnhDataGrid v-else ref="paymentGridRef" :columns="columns" :row-data="paymentRows" :show-pagination-counter="true" :pagination-size-selector="[5, 10, 20, 50]" :pagination-button-count="0" items-name="Payments" :pagination="20" column-layout="fitColumns" />
        </template>
    </div>

    <!-- Parent Offices -->
    <div v-show="selectedTab === 1">
        <div v-if="!hasParents" class="panel well empty-notice">
            <p><span class="glyphicon glyphicon-remove-circle" aria-hidden="true"></span></p>
            <h2>No parent offices to display.</h2>
            <p>Do you need to adjust your filters above?</p>
        </div>
        <CmnhDataGrid v-else ref="parentLocationsGridRef" :columns="parentLocationColumns" :row-data="parentRows" :show-pagination-counter="true" :pagination-size-selector="[5, 10, 20, 50]" :pagination-button-count="0" items-name="Parent Offices" :pagination="20" column-layout="fitColumns" />
    </div>

    <!-- Offices -->
    <div v-show="selectedTab === 2">
        <div v-if="!hasLocations" class="panel well empty-notice">
            <p><span class="glyphicon glyphicon-remove-circle" aria-hidden="true"></span></p>
            <h2>No offices to display.</h2>
            <p>Do you need to adjust your filters above?</p>
        </div>
        <CmnhDataGrid v-else ref="locationGridRef" :columns="locationColumns" :row-data="locationRows" :show-pagination-counter="true" :pagination-size-selector="[5, 10, 20, 50]" :pagination-button-count="0" items-name="Offices" :pagination="20" column-layout="fitColumns" />
    </div>
</template>
